// Sprint 17 A5: Enrichment Waterfall — sources, confidence, re-enrich
import {
  Database, Globe, Link2, Server, Brain,
  Check, X, AlertCircle, Loader2, RefreshCw,
} from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useCrmEnrichment, useCrmEnrich } from "@/hooks/use-crm-enrichment";
import type { EnrichmentSource } from "@/hooks/use-crm-enrichment";

interface EnrichmentPanelProps {
  contactId: string;
}

const SOURCE_ICONS: Record<string, { Icon: typeof Database; color: string }> = {
  own_data: { Icon: Database, color: "text-[var(--color-success)]" },
  website_crawl: { Icon: Globe, color: "text-[var(--color-primary)]" },
  linkedin: { Icon: Link2, color: "text-[var(--color-info)]" },
  public_db: { Icon: Server, color: "text-[var(--color-muted-foreground)]" },
  ai_inference: { Icon: Brain, color: "text-[var(--color-ai)]" },
};

const STATUS_ICONS: Record<string, { Icon: typeof Check; color: string; label: string }> = {
  found: { Icon: Check, color: "text-[var(--color-success)]", label: "Data found" },
  not_found: { Icon: X, color: "text-[var(--color-muted-foreground)]", label: "No data" },
  error: { Icon: AlertCircle, color: "text-[var(--color-destructive)]", label: "Error" },
  pending: { Icon: Loader2, color: "text-[var(--color-warning)]", label: "Pending" },
};

export function EnrichmentPanel({ contactId }: EnrichmentPanelProps) {
  const { data, isLoading } = useCrmEnrichment(contactId);
  const { enrich: triggerEnrich, isLoading: isEnriching } = useCrmEnrich(contactId);

  if (isLoading) return <EnrichmentSkeleton />;
  if (!data?.sources) return null;

  const handleReEnrich = async () => {
    await triggerEnrich();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Enrichment Waterfall</h3>
        <div className="flex items-center gap-2">
          {data.enrichedAt && (
            <span className="text-[10px] text-[var(--color-muted-foreground)]">
              Last: {formatTimeAgo(data.enrichedAt)}
            </span>
          )}
          <button
            onClick={handleReEnrich}
            disabled={isEnriching}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-[var(--color-border)] text-[var(--color-foreground)] hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)] disabled:opacity-50 transition-colors"
          >
            {isEnriching ? (
              <>
                <Skeleton className="h-3 w-3" />
                <Skeleton className="h-3 w-14" />
              </>
            ) : (
              <>
                <RefreshCw className="h-3 w-3" />
                Re-enrich
              </>
            )}
          </button>
        </div>
      </div>

      {/* Waterfall: sources tried in sequence */}
      <div className="space-y-2">
        {data.sources.map((source, idx) => (
          <WaterfallRow key={source.source} source={source} index={idx} />
        ))}
      </div>

      {/* Enriched fields summary */}
      {Object.keys(data.fields).length > 0 && (
        <div className="border-t border-[var(--color-border)] pt-3">
          <h4 className="text-xs font-semibold text-[var(--color-muted-foreground)] mb-2">Enriched Fields</h4>
          <div className="space-y-1.5">
            {Object.entries(data.fields).map(([key, field]) => {
              const sourceConfig = SOURCE_ICONS[field.source];
              return (
                <div key={key} className="stagger-item flex items-center justify-between border-b border-[var(--color-border)]/50 py-1 last:border-0">
                  <span className="text-xs text-[var(--color-muted-foreground)] capitalize">{key.replace(/([A-Z])/g, " $1")}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-[var(--color-foreground)]">{String(field.value)}</span>
                    {sourceConfig && (
                      <sourceConfig.Icon className={`h-3 w-3 ${sourceConfig.color}`} />
                    )}
                    <ConfidenceDot confidence={field.confidence} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function WaterfallRow({ source, index }: { source: EnrichmentSource; index: number }) {
  const iconConfig = SOURCE_ICONS[source.source] ?? { Icon: Database, color: "text-[var(--color-muted-foreground)]" };
  const SourceIcon = iconConfig.Icon;
  const statusConfig = STATUS_ICONS[source.status] ?? { Icon: AlertCircle, color: "text-[var(--color-destructive)]", label: "Error" };
  const StatusIcon = statusConfig.Icon;
  const isPending = source.status === "pending";

  return (
    <div className="stagger-item flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      {/* Step number */}
      <span className="text-[10px] font-bold text-[var(--color-muted-foreground)] w-4 text-center">{index + 1}</span>

      {/* Source icon */}
      <SourceIcon className={`h-4 w-4 flex-shrink-0 ${iconConfig.color}`} />

      {/* Source info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-[var(--color-foreground)]">{source.label}</span>
          {isPending ? <Skeleton className="h-3 w-3" /> : <StatusIcon className={`h-3 w-3 ${statusConfig.color}`} />}
        </div>
        {source.fieldsFound.length > 0 && (
          <p className="text-[10px] text-[var(--color-muted-foreground)] truncate">
            Found: {source.fieldsFound.join(", ")}
          </p>
        )}
      </div>

      {/* Confidence + response time */}
      <div className="text-right flex-shrink-0">
        {source.status === "found" && (
          <ConfidenceDot confidence={source.confidence} />
        )}
        {source.responseTimeMs != null && (
          <p className="text-[9px] text-[var(--color-muted-foreground)]">{source.responseTimeMs}ms</p>
        )}
      </div>
    </div>
  );
}

function ConfidenceDot({ confidence }: { confidence: number }) {
  const color = confidence >= 0.8 ? "text-[var(--color-success)]"
    : confidence >= 0.5 ? "text-[var(--color-warning)]"
    : "text-[var(--color-muted-foreground)]";
  const label = `${Math.round(confidence * 100)}% confidence`;

  return (
    <span className={`text-[10px] font-medium ${color}`} title={label}>
      {confidence >= 0.8 ? "●" : confidence >= 0.5 ? "◐" : "○"}
    </span>
  );
}

function EnrichmentSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-4 w-32" />
      {[1, 2, 3, 4, 5].map((i) => (
        <SkeletonCard key={i} className="space-y-2" />
      ))}
    </div>
  );
}

function formatTimeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
